import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import TableContextMenu from '../../../../common/components/Table/ContextMenu';
import { ContextMenuAction } from '../../../../common/components/Table/types';
import {
  StyledTableContainer,
  StyledTable,
  HeaderCell,
  ComplexHeaderCell,
  SubHeaderCell,
  DataCell,
  ComplexDataCell,
  GroupSeparatorRow,
  TotalSectionHeaderRow,
} from './Cn54bisCn56SummaryTable.styled';

export interface Cn54bisCn56SummaryData {
  sendingPpo: string;
  receivingPpo: string;
  year: string;
  service: string;
  mailCategory: string;
  format: string;
  product: string;
  // CN56 fields - from backend (can be formatted strings or numbers)
  originalItems: number | string | null;
  bilateralItems: number | string | null;
  originalGrossWeight: number | string | null;
  bilateralGrossWeight: number | string | null;
  // CN54bis fields - from backend (can be formatted strings or numbers)
  originalGrossIpk: number | string | null;
  bilateralGrossIpk: number | string | null;
  originalGross: number | string | null;
  bilateralGross: number | string | null;
  // Additional Gross % fields for connected rows (4, 5, 6)
  originalGrossP?: number | null;
  bilateralGrossP?: number | null;
  originalGrossG?: number | null;
  bilateralGrossG?: number | null;
  originalGrossE?: number | null;
  bilateralGrossE?: number | null;
  // Additional fields from new backend structure
  cn54bisCn56SummaryRowType?: string;
  mailCategoryService?: string;
  mailCategoryDbValue?: string;
  summaryKey?: string;
  productAsString?: string;
  direction?: 'inbound' | 'outbound';
  rowIndex?: number; // Index of the row within its group/block
  accountingType?: string; // Add accounting type for disabled field logic
  alerts?: string[]; // Array of alert codes
  // Connected rows data for editing (rows 4, 5, 6)
  connectedRows?: Cn54bisCn56SummaryData[];
}

// Utility function to determine which fields should be disabled for CN54bis CN56 Summary
export function getDisabledFieldsCn54bis(accountingType: string, rowIndex: number, isTotal: boolean = false) {
  // Default all fields to enabled
  const disabled = {
    cn56ItemsOriginal: false,
    cn56ItemsBilateral: false,
    cn56GrossWeightOriginal: false,
    cn56GrossWeightBilateral: false,
    cn54bisGrossIpkOriginal: false,
    cn54bisGrossIpkBilateral: false,
    cn54bisGrossOriginal: false,
    cn54bisGrossBilateral: false,
  };

  if (isTotal) {
    // Apply special disabling rules for "total" section rows
    switch (rowIndex) {
      case 0: // 1st row
      case 1: // 2nd row  
      case 2: // 3rd row
        // Items, Gross Weight(bilateral), Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 3: // 4th row
      case 4: // 5th row
      case 5: // 6th row
        // Items, Gross Weight(bilateral), Gross IPK(bilateral), Gross%(bilateral)
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 6: // 7th row
        // Items, Gross Weight(bilateral), Gross IPK(bilateral), Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 7: // 8th row
      case 8: // 9th row
        // Items(bilateral), Gross Weight(bilateral), Gross IPK, Gross%
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 9: // 10th row
        // Items, Gross Weight(bilateral), Gross IPK, Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 10: // 11th row
      case 11: // 12th row
      case 12: // 13th row
        // Items(bilateral), Gross Weight, Gross IPK, Gross%
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightOriginal = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 13: // 14th row
      case 14: // 15th row
      case 15: // 16th row
      case 16: // 17th row
        // Items, Gross Weight(bilateral), Gross IPK, Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      default:
        // For any other rows, keep all fields enabled
        break;
    }
  } else {
    // Apply regular disabling rules based on row index (0-based)
    switch (rowIndex) {
      case 0: // 1st row
      case 1: // 2nd row  
      case 2: // 3rd row
        // Items and Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 3: // 4th row
      case 4: // 5th row
      case 5: // 6th row
        // Items
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        break;
      
      case 6: // 7th row
        // Items, Gross IPK (original), Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 7: // 8th row
      case 8: // 9th row
        // Gross IPK, Gross%
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 9: // 10th row
        // Items, Gross IPK, Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 10: // 11th row
      case 11: // 12th row
      case 12: // 13th row
        // Gross Weight, Gross IPK, Gross%
        disabled.cn56GrossWeightOriginal = true;
        disabled.cn56GrossWeightBilateral = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      case 13: // 14th row
      case 14: // 15th row
      case 15: // 16th row
      case 16: // 17th row
        // Items, Gross Weight (Original), Gross IPK, Gross%
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56GrossWeightOriginal = true;
        disabled.cn54bisGrossIpkOriginal = true;
        disabled.cn54bisGrossIpkBilateral = true;
        disabled.cn54bisGrossOriginal = true;
        disabled.cn54bisGrossBilateral = true;
        break;
      
      default:
        // For any other rows, keep all fields enabled
        break;
    }
  }

  return disabled;
}

interface GroupedData {
  key: string;
  items: Cn54bisCn56SummaryData[];
}

interface Cn54bisCn56SummaryTableProps {
  data: any;
  isLoading: boolean;
  onEditRecord?: (row: Cn54bisCn56SummaryData) => void;
  onResetRecord?: (row: Cn54bisCn56SummaryData) => void;
  onResetBlock?: (groupKey: string, direction: 'inbound' | 'outbound') => void;
  onDownloadPdf?: (row: Cn54bisCn56SummaryData) => void;
  onDownloadXls?: (row: Cn54bisCn56SummaryData) => void;
}

// Helper component for rendering a single table
const SummaryTable = ({ 
  groupedData, 
  title, 
  direction,
  onContextMenu 
}: { 
  groupedData: GroupedData[]; 
  title: string; 
  direction: 'inbound' | 'outbound';
  onContextMenu: (
    event: React.MouseEvent,
    row: Cn54bisCn56SummaryData,
    groupKey: string,
    direction: 'inbound' | 'outbound'
  ) => void;
}) => {
  if (!groupedData.length) return null;

  // Helper function to determine error states based on business rules
  const getErrorStates = (row: Cn54bisCn56SummaryData) => {
    const errors = {
      originalGrossIpkError: false,
      originalGrossWeightError: false
    };

    // Check if row <= 6 (product = 'UA, UE, UL, UN, UY, UZ')
    const isTargetProduct = row.productAsString && ['UA', 'UE', 'UL', 'UN', 'UY', 'UZ'].includes(row.productAsString);
    
    if (isTargetProduct) {
      // Check if (OriginalGrossWeight or bilateralGrossWeight exists) and (originalGrossIpk and bilateralGrossIpk is null)
      const hasGrossWeight = (row.originalGrossWeight !== null && row.originalGrossWeight !== undefined) || 
                            (row.bilateralGrossWeight !== null && row.bilateralGrossWeight !== undefined);
      const bothIpkNull = row.originalGrossIpk === null && row.bilateralGrossIpk === null;
      
      if (hasGrossWeight && bothIpkNull) {
        errors.originalGrossIpkError = true;
        console.log(`Validation Error - Row ${row.rowIndex}: IPK error for product ${row.productAsString}`, {
          originalGrossWeight: row.originalGrossWeight,
          bilateralGrossWeight: row.bilateralGrossWeight,
          originalGrossIpk: row.originalGrossIpk,
          bilateralGrossIpk: row.bilateralGrossIpk
        });
      }

      // Check if (originalGrossIpk or bilateralGrossIpk exists) and (originalGrossIpk and bilateralGrossIpk is 0)
      const hasIpk = (row.originalGrossIpk !== null && row.originalGrossIpk !== undefined) || 
                     (row.bilateralGrossIpk !== null && row.bilateralGrossIpk !== undefined);
      const bothIpkZero = row.originalGrossIpk === 0 && row.bilateralGrossIpk === 0;
      
      if (hasIpk && bothIpkZero) {
        errors.originalGrossWeightError = true;
        console.log(`Validation Error - Row ${row.rowIndex}: Gross Weight error for product ${row.productAsString}`, {
          originalGrossWeight: row.originalGrossWeight,
          bilateralGrossWeight: row.bilateralGrossWeight,
          originalGrossIpk: row.originalGrossIpk,
          bilateralGrossIpk: row.bilateralGrossIpk
        });
      }
    }

    return errors;
  };

  return (
    <Box sx={{ mb: 4 }} data-testid="cn54bis-cn56-summary-table-container">
      <StyledTableContainer data-testid="cn54bis-cn56-summary-table-container-paper">
        <StyledTable data-testid="cn54bis-cn56-summary-table">
          <TableHead data-testid="cn54bis-cn56-summary-table-head">
            {/* Level 1: Main group headers */}
            <TableRow>
              {/* Simple columns - each spans 1 column */}
              <HeaderCell rowSpan={3}>Sending DO</HeaderCell>
              <HeaderCell rowSpan={3}>Receiving DO</HeaderCell>
              <HeaderCell rowSpan={3}>Year</HeaderCell>
              <HeaderCell rowSpan={3}>Service</HeaderCell>
              <HeaderCell rowSpan={3}>Mail Category</HeaderCell>
              <HeaderCell rowSpan={3}>Format</HeaderCell>
              <HeaderCell rowSpan={3}>Product</HeaderCell>
              
              {/* CN56 spans 4 columns (Items + Gross Weight only) */}
              <ComplexHeaderCell colSpan={4} isGroupBorder>CN56</ComplexHeaderCell>
              
              {/* CN54bis spans 4 columns */}
              <ComplexHeaderCell colSpan={4} isGroupBorder>CN54bis</ComplexHeaderCell>
            </TableRow>
            
            {/* Level 2: Sub-group headers */}
            <TableRow>
              {/* CN56 sub-headers */}
              <SubHeaderCell colSpan={2} isGroupBorder>Items</SubHeaderCell>
              <SubHeaderCell colSpan={2} isGroupBorder>Gross Weight</SubHeaderCell>
              
              {/* CN54bis sub-headers */}
              <SubHeaderCell colSpan={2} isGroupBorder>Gross IPK</SubHeaderCell>
              <SubHeaderCell colSpan={2} isGroupBorder isRightGroupBorder>Gross %</SubHeaderCell>
            </TableRow>
            
            {/* Level 3: Individual column headers */}
            <TableRow>
              {/* CN56 Items */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* CN56 Gross Weight */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* CN54bis Gross IPK */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* CN54bis Gross % */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell isRightGroupBorder>Bilateral</SubHeaderCell>
            </TableRow>
          </TableHead>
          
          <TableBody data-testid="cn54bis-cn56-summary-table-body">
            {groupedData
              .filter((group) => group.items && group.items.length > 0)
              .map((group, groupIndex) => {
                // Filter rows first to get valid rows
                const validRows = group.items.filter((row: Cn54bisCn56SummaryData) => {
                  // Only show rows that have meaningful data - at least one non-null value
                  const hasData = row.sendingPpo || row.receivingPpo || row.year || 
                         row.originalItems !== null || row.bilateralItems !== null ||
                         row.originalGrossWeight !== null || row.bilateralGrossWeight !== null ||
                         row.originalGrossIpk !== null || row.bilateralGrossIpk !== null ||
                         row.originalGross !== null || row.bilateralGross !== null;
                  
                  // Debug log to see what's being filtered
                  if (!hasData) {
                    console.log('Filtering out row:', row);
                  }
                  
                  return hasData;
                });

                // Check if there are any total rows in this group
                const hasTotalRows = validRows.some((row: Cn54bisCn56SummaryData) => row.mailCategoryService === 'total');
                // Find the index of the first total row
                const firstTotalRowIndex = validRows.findIndex((row: Cn54bisCn56SummaryData) => row.mailCategoryService === 'total');

                return (
                  <React.Fragment key={group.key}>
                    {validRows.map((row: Cn54bisCn56SummaryData, index: number) => {
                      // Check if this is the first total row (transition from regular to total)
                      const isFirstTotalRow = hasTotalRows && 
                                             row.mailCategoryService === 'total' && 
                                             index === firstTotalRowIndex;
                      
                      return (
                        <React.Fragment key={`${group.key}-${index}`}>
                          {/* Insert header row before the first total row - matches main table header structure */}
                          {isFirstTotalRow && (
                            <>
                              {/* Level 1: Main group headers - show simple columns, skip CN56 and CN54bis main headers */}
                              <TotalSectionHeaderRow>
                                <HeaderCell rowSpan={2}>
                                  Sending DO
                                </HeaderCell>
                                <HeaderCell rowSpan={2}>
                                  Receiving DO
                                </HeaderCell>
                                <HeaderCell rowSpan={2}>
                                  Year
                                </HeaderCell>
                                <HeaderCell rowSpan={2}>
                                  Service
                                </HeaderCell>
                                <HeaderCell rowSpan={2}>
                                  Mail Category
                                </HeaderCell>
                                <HeaderCell rowSpan={2}>
                                  Format
                                </HeaderCell>
                                <HeaderCell rowSpan={2}>
                                  Product
                                </HeaderCell>
                                {/* Empty cells spanning CN56 and CN54bis main headers (4 columns each, 8 total) */}
                                <HeaderCell colSpan={4} />
                                <HeaderCell colSpan={4} />
                              </TotalSectionHeaderRow>
                              
                              {/* Level 2: Sub-group headers - show Items, Gross Weight, Gross IPK, Gross %} */}
                              <TotalSectionHeaderRow>
                                {/* CN56 sub-headers */}
                                <SubHeaderCell colSpan={2} isGroupBorder>
                                  Items
                                </SubHeaderCell>
                                <SubHeaderCell colSpan={2} isGroupBorder>
                                  Gross Weight
                                </SubHeaderCell>
                                
                                {/* CN54bis sub-headers */}
                                <SubHeaderCell colSpan={2} isGroupBorder>
                                  Gross IPK
                                </SubHeaderCell>
                                <SubHeaderCell colSpan={2} isGroupBorder isRightGroupBorder>
                                  Gross %
                                </SubHeaderCell>
                              </TotalSectionHeaderRow>
                            </>
                          )}
                          
                          {/* Helper function to check if both original and bilateral values are empty */}
                          {(() => {
                            const isBothEmpty = (original: number | null, bilateral: number | null) => {
                              return original === null && bilateral === null;
                            };

                            // Get error states for this row
                            const errorStates = getErrorStates(row);

                            // Get disabled fields for this row
                            const isTotal = row.mailCategoryService === 'total';
                            const disabledFields = getDisabledFieldsCn54bis(row.accountingType || 'G', row.rowIndex || 0, isTotal);
                            
                            // Check if this is the first row of the group
                            const isFirstRowOfGroup = index === 0;

                            // Debug log to see what rows are being rendered
                            console.log('Rendering row:', row);

                            return (
                    <TableRow 
                      key={`${group.key}-${index}`}
                      data-testid={`cn54bis-cn56-summary-table-row-${group.key}-${index}`}
                      onContextMenu={(event) => onContextMenu(event, row, group.key, direction)}
                      sx={{ 
                        cursor: 'context-menu',
                        '&:hover': {
                          backgroundColor: direction === 'inbound' 
                            ? 'rgba(76, 175, 80, 0.1)' 
                            : 'rgba(244, 67, 54, 0.1)',
                        }
                      }}
                    >
                      {/* Simple columns */}
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.sendingPpo || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.receivingPpo || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.year || ''}</DataCell>
                      
                      {/* Service and Mail Category - merge when mailCategoryService is "total" */}
                      {row.mailCategoryService === 'total' ? (
                        <DataCell 
                          direction={direction} 
                          isFirstRowOfGroup={isFirstRowOfGroup}
                          colSpan={2}
                          sx={{ 
                            textAlign: 'center',
                            fontWeight: 600,
                            color: '#E65100'
                          }}
                        >
                          {row.mailCategoryService}
                        </DataCell>
                      ) : (
                        <>
                          <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.service || ''}</DataCell>
                          <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.mailCategory || ''}</DataCell>
                        </>
                      )}
                      
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.format || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>
                        {row.productAsString || ''}
                      </DataCell>
                      
                      {/* CN56 Items */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={row.bilateralItems !== null && row.bilateralItems !== undefined && row.bilateralItems !== ''}
                        isDisabled={disabledFields.cn56ItemsOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertA={
                          row.mailCategoryService === 'total'
                            ? (row.alerts?.includes('C') || false)
                            : false
                        }
                      >
                        {row.originalItems !== null && row.originalItems !== undefined ? row.originalItems : ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.cn56ItemsBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.bilateralItems !== null && row.bilateralItems !== undefined ? row.bilateralItems : ''}
                      </ComplexDataCell>
                      
                      {/* CN56 Gross Weight */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={row.bilateralGrossWeight !== null && row.bilateralGrossWeight !== undefined && row.bilateralGrossWeight !== ''}
                        hasError={errorStates.originalGrossWeightError}
                        isDisabled={disabledFields.cn56GrossWeightOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertA={
                          row.mailCategoryService === 'total'
                            ? (row.alerts?.includes('A') || row.alerts?.includes('C') || false)
                            : (row.alerts?.includes('A') || false)
                        }
                        title={errorStates.originalGrossWeightError ? "Error: Both IPK values are 0 when IPK data exists" : ""}
                      >
                        {row.originalGrossWeight !== null && row.originalGrossWeight !== undefined ? row.originalGrossWeight : ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.cn56GrossWeightBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.bilateralGrossWeight !== null && row.bilateralGrossWeight !== undefined ? row.bilateralGrossWeight : ''}
                      </ComplexDataCell>
                      
                      {/* CN54bis Gross IPK */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={row.bilateralGrossIpk !== null && row.bilateralGrossIpk !== undefined && row.bilateralGrossIpk !== ''}
                        hasError={errorStates.originalGrossIpkError}
                        isDisabled={disabledFields.cn54bisGrossIpkOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertB={row.alerts?.includes('B') || false}
                        title={errorStates.originalGrossIpkError ? "Error: Both IPK values are null when Gross Weight data exists" : ""}
                      >
                        {row.originalGrossIpk !== null && row.originalGrossIpk !== undefined ? row.originalGrossIpk : ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.cn54bisGrossIpkBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.bilateralGrossIpk !== null && row.bilateralGrossIpk !== undefined ? row.bilateralGrossIpk : ''}
                      </ComplexDataCell>
                      
                      {/* CN54bis Gross % */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={row.bilateralGross !== null && row.bilateralGross !== undefined && row.bilateralGross !== ''}
                        isDisabled={disabledFields.cn54bisGrossOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.originalGross !== null && row.originalGross !== undefined ? row.originalGross : ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.cn54bisGrossBilateral}
                        isRightGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.bilateralGross !== null && row.bilateralGross !== undefined ? row.bilateralGross : ''}
                      </ComplexDataCell>
                    </TableRow>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}
                {/* Add bold separator line at the end of each group (except the last group) */}
                {groupIndex < groupedData.length - 1 && (
                  <GroupSeparatorRow>
                    <TableCell 
                      colSpan={
                        // Check if any row in this group has merged columns (mailCategory === 'total')
                        group.items.some(row => row.mailCategoryService === 'total') ? 14 : 15
                      } 
                    />
                  </GroupSeparatorRow>
                )}
              </React.Fragment>
                );
              })}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      
    </Box>
  );
};

export function Cn54bisCn56SummaryTable({ 
  data, 
  isLoading,
  onEditRecord,
  onResetRecord,
  onResetBlock,
  onDownloadPdf,
  onDownloadXls
}: Cn54bisCn56SummaryTableProps) {
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    row: Cn54bisCn56SummaryData | null;
    groupKey: string;
    direction: 'inbound' | 'outbound';
  } | null>(null);

  // Handle context menu open
  const handleContextMenu = useCallback((
    event: React.MouseEvent,
    row: Cn54bisCn56SummaryData,
    groupKey: string,
    direction: 'inbound' | 'outbound'
  ) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      row,
      groupKey,
      direction,
    });
  }, []);

  // Handle context menu close
  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Handle context menu actions
  const handleContextMenuAction = useCallback((action: ContextMenuAction) => {
    if (!contextMenu?.row) return;

    const { row, groupKey, direction } = contextMenu;

    switch (action) {
      case 'editRecord':
        onEditRecord?.(row);
        break;
      case 'resetRecord':
        onResetRecord?.(row);
        break;
      case 'resetBlock':
        onResetBlock?.(groupKey, direction);
        break;
      case 'downloadPdf':
        onDownloadPdf?.(row);
        break;
      case 'downloadXls':
        onDownloadXls?.(row);
        break;
      default:
        console.log('Unhandled action:', action);
    }
    
    setContextMenu(null);
  }, [contextMenu, onEditRecord, onResetRecord, onResetBlock, onDownloadPdf, onDownloadXls]);

  // Transform the data structure for the table, preserving grouping
  const { inboundGroupedData, outboundGroupedData } = useMemo(() => {
    if (!data) return { inboundGroupedData: [], outboundGroupedData: [] };
    
    const inboundGrouped: GroupedData[] = [];
    const outboundGrouped: GroupedData[] = [];
    
    // Helper function to process summary tables
    const processSummaryTables = (summaryTables: any[], direction: 'inbound' | 'outbound') => {
      const groupedByKey: { [key: string]: any[] } = {};
      
      summaryTables.forEach((summary: any) => {
        const key = `${summary.sendingPpo}_${summary.receivingPpo}_${summary.year}_${summary.mailCategory}`;
        
        if (!groupedByKey[key]) {
          groupedByKey[key] = [];
        }
        
        // Transform each summary row into the expected format
        const transformedRow = {
          sendingPpo: summary.sendingPpo,
          receivingPpo: summary.receivingPpo,
          year: summary.year.toString(),
          service: summary.mailCategoryService,
          mailCategory: summary.mailCategory || 'Unknown',
          format: summary.format,
          product: summary.productAsString || 'CN54bis_CN56_Summary',
          // CN56 fields - use formatted values from backend
          originalItems: summary.originalItemsFormatted !== undefined && summary.originalItemsFormatted !== null ? summary.originalItemsFormatted : summary.originalItems,
          bilateralItems: summary.bilateralItemsFormatted !== undefined && summary.bilateralItemsFormatted !== null ? summary.bilateralItemsFormatted : summary.bilateralItems,
          originalGrossWeight: summary.originalGrossWeightFormatted !== undefined && summary.originalGrossWeightFormatted !== null ? summary.originalGrossWeightFormatted : summary.originalGrossWeight,
          bilateralGrossWeight: summary.bilateralGrossWeightFormatted !== undefined && summary.bilateralGrossWeightFormatted !== null ? summary.bilateralGrossWeightFormatted : summary.bilateralGrossWeight,
          // CN54bis fields - use formatted values from backend
          originalGrossIpk: summary.originalGrossIpkFormatted !== undefined && summary.originalGrossIpkFormatted !== null ? summary.originalGrossIpkFormatted : summary.originalGrossIpk,
          bilateralGrossIpk: summary.bilateralGrossIpkFormatted !== undefined && summary.bilateralGrossIpkFormatted !== null ? summary.bilateralGrossIpkFormatted : summary.bilateralGrossIpk,
          originalGross: summary.originalGrossFormatted !== undefined && summary.originalGrossFormatted !== null ? summary.originalGrossFormatted : summary.originalGross,
          bilateralGross: summary.bilateralGrossFormatted !== undefined && summary.bilateralGrossFormatted !== null ? summary.bilateralGrossFormatted : summary.bilateralGross,
          direction: direction,
          rowIndex: 0,
          // Additional fields from new structure
          cn54bisCn56SummaryRowType: summary.cn54bisCn56SummaryRowType,
          mailCategoryService: summary.mailCategoryService,
          mailCategoryDbValue: summary.mailCategoryDbValue,
          summaryKey: summary.summaryKey,
          productAsString: summary.productAsString,
          alerts: summary.alerts || []
        };
        
        groupedByKey[key].push(transformedRow);
      });
      
      const grouped: GroupedData[] = [];
      // Convert grouped data to the expected format
      Object.entries(groupedByKey).forEach(([key, rows]) => {
        // Sort rows by cn54bisCn56SummaryRowType to maintain logical order
        rows.sort((a, b) => {
          const aNum = parseInt(a.cn54bisCn56SummaryRowType?.replace('ROW_', '') || '0');
          const bNum = parseInt(b.cn54bisCn56SummaryRowType?.replace('ROW_', '') || '0');
          return aNum - bNum;
        });
        
        // Update rowIndex after sorting
        rows.forEach((row, index) => {
          row.rowIndex = index;
        });

        // Populate connectedRows for rows 4, 5, 6 (indices 3, 4, 5) so they can access each other's data
        const connectedIndices = [3, 4, 5];
        const connectedRows = rows.filter(r => r.rowIndex !== undefined && connectedIndices.includes(r.rowIndex));
        
        if (connectedRows.length > 0) {
          connectedRows.forEach(row => {
            row.connectedRows = connectedRows;
          });
        }
        
        grouped.push({
          key,
          items: rows
        });
      });
      
      return grouped;
    };
    
    // Process inbound data
    if (data.inbound && data.inbound.summaryTables) {
      const processedInbound = processSummaryTables(data.inbound.summaryTables, 'inbound');
      inboundGrouped.push(...processedInbound);
    }
    
    // Process outbound data
    if (data.outbound && data.outbound.summaryTables) {
      const processedOutbound = processSummaryTables(data.outbound.summaryTables, 'outbound');
      outboundGrouped.push(...processedOutbound);
    }
    
    return { inboundGroupedData: inboundGrouped, outboundGroupedData: outboundGrouped };
  }, [data]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!inboundGroupedData.length && !outboundGroupedData.length) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: '#FFF8E1',
          borderRadius: '4px',
          border: '1px dashed #FFE082',
        }}
      >
        <Typography
          sx={{
            fontStyle: 'italic',
            color: '#F57F17',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          No CN54bis & CN56 Summary data available
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ mt: 2, p: 2 }}>
      
      {/* Inbound Table */}
      {inboundGroupedData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4CAF50' }}>
            Inbound Data
          </Typography>
          
          {/* Inbound Files Table */}
          {data?.inbound?.files && data.inbound.files.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#063142' }}>
                Inbound Files Information
              </Typography>
              <Paper sx={{ p: 2 }}>
                {/* Header Row */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 2fr 1fr 2fr 1fr',
                  gap: 2,
                  p: 1,
                  backgroundColor: '#F8F9FA',
                  borderRadius: '4px',
                  mb: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    Acc. Q.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e53 Filename
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e53 Status
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e55 Filename
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e55 Status
                  </Typography>
                </Box>
                
                {/* Data Rows */}
                {data.inbound.files.map((file: any, index: number) => (
                  <Box key={index} sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 2fr 1fr 2fr 1fr',
                    gap: 2,
                    p: 1,
                    borderBottom: index < data.inbound.files.length - 1 ? '1px solid #E9ECEF' : 'none',
                    '&:hover': {
                      backgroundColor: '#F8F9FA'
                    }
                  }}>
                    <Typography variant="body2" sx={{ color: '#495057' }}>
                      {file.accountingQuarter}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057', fontFamily: 'monospace' }}>
                      {file.e53FileName || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057' }}>
                      {file.e53FileStatus || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057', fontFamily: 'monospace' }}>
                      {file.e55FileName || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057' }}>
                      {file.e55FileStatus || ''}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
          
          <SummaryTable 
            groupedData={inboundGroupedData} 
            title="CN54bis & CN56 Summary - Inbound" 
            direction="inbound"
            onContextMenu={handleContextMenu}
          />
        </Box>
      )}
      
      {/* Outbound Table */}
      {outboundGroupedData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#F44336' }}>
            Outbound Data
          </Typography>
          
          {/* Outbound Files Table */}
          {data?.outbound?.files && data.outbound.files.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#063142' }}>
                Outbound Files Information
              </Typography>
              <Paper sx={{ p: 2 }}>
                {/* Header Row */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 2fr 1fr 2fr 1fr',
                  gap: 2,
                  p: 1,
                  backgroundColor: '#F8F9FA',
                  borderRadius: '4px',
                  mb: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    Acc. Q.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e53 Filename
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e53 Status
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e55 Filename
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#063142' }}>
                    e55 Status
                  </Typography>
                </Box>
                
                {/* Data Rows */}
                {data.outbound.files.map((file: any, index: number) => (
                  <Box key={index} sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 2fr 1fr 2fr 1fr',
                    gap: 2,
                    p: 1,
                    borderBottom: index < data.outbound.files.length - 1 ? '1px solid #E9ECEF' : 'none',
                    '&:hover': {
                      backgroundColor: '#F8F9FA'
                    }
                  }}>
                    <Typography variant="body2" sx={{ color: '#495057' }}>
                      {file.accountingQuarter}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057', fontFamily: 'monospace' }}>
                      {file.e53FileName || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057' }}>
                      {file.e53FileStatus || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057', fontFamily: 'monospace' }}>
                      {file.e55FileName || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#495057' }}>
                      {file.e55FileStatus || ''}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
          
          <SummaryTable 
            groupedData={outboundGroupedData} 
            title="CN54bis & CN56 Summary - Outbound" 
            direction="outbound"
            onContextMenu={handleContextMenu}
          />
        </Box>
      )}


      {/* Context Menu */}
      <TableContextMenu
        open={contextMenu !== null}
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : null
        }
        onClose={handleContextMenuClose}
        onAction={handleContextMenuAction}
        hideDefaultActions={true}
        customActions={
          // Show different actions based on whether it's a total row or not
          contextMenu?.row?.mailCategoryService === 'total'
            ? [
                // Total rows: only download options
                {
                  action: 'downloadPdf',
                  label: 'Download PDF',
                  icon: <PictureAsPdfIcon sx={{ fontSize: '16px' }} />,
                },
                {
                  action: 'downloadXls',
                  label: 'Download XLS',
                  icon: <TableViewIcon sx={{ fontSize: '16px' }} />,
                },
              ]
            : [
                // Regular rows: all options
                {
                  action: 'editRecord',
                  label: 'Edit Record',
                  icon: <EditIcon sx={{ fontSize: '16px' }} />,
                },
                {
                  action: 'resetRecord',
                  label: 'Reset Record',
                  icon: <RestoreIcon sx={{ fontSize: '16px' }} />,
                },
                {
                  action: 'resetBlock',
                  label: 'Reset Block',
                  icon: <RefreshIcon sx={{ fontSize: '16px' }} />,
                },
                {
                  action: 'downloadPdf',
                  label: 'Download PDF',
                  icon: <PictureAsPdfIcon sx={{ fontSize: '16px' }} />,
                },
                {
                  action: 'downloadXls',
                  label: 'Download XLS',
                  icon: <TableViewIcon sx={{ fontSize: '16px' }} />,
                },
              ]
        }
        currentRow={contextMenu?.row}
      />
    </Paper>
  );
} 